/**
 * "Is the setup wizard currently on screen?" — a mount-presence signal, and
 * nothing else.
 *
 * ## Why this exists rather than a route check
 *
 * The setup REMINDER banner (`SetupReminderGate`) must not nag a user who is
 * already standing on the wizard it points at — the operator's words:
 * *"the popup offering to complete the steps should not show if the wizard is
 * already running"*.
 *
 * That suppression already existed, keyed on `shouldGateSetup(status)` — i.e.
 * *"is the BLOCKING gate active?"* (`requiredBlockingOutstanding.length > 0`).
 * Those two questions coincided until a user appeared whose outstanding steps
 * were ALL optional: the blocking gate is off, the guard passes, and the banner
 * fires while the wizard is visibly open. Measured 2026-08-10 on the first user
 * created after the P6 reset, whose remaining steps were Telegram + assistant
 * mailbox — both optional.
 *
 * ⚠ **Deliberately NOT `pathname.startsWith("/identity")`.** `/identity` is
 * reachable without the wizard (it is also the identity editor), so a route
 * check suppresses the banner where it should still fire; and it silently stops
 * working the moment the wizard renders anywhere else. Presence is the fact the
 * requirement is actually about, so presence is what gets recorded.
 *
 * Counted, not boolean: two wizard instances briefly overlapping during a route
 * transition must not have the first unmount clear a flag the second still
 * needs. A plain boolean would flicker the banner back on mid-transition.
 */

import { create } from "zustand";

interface SetupWizardPresenceState {
  /** Number of mounted wizard instances. >0 means "on screen". */
  mountedCount: number;
  acquire: () => void;
  release: () => void;
}

export const useSetupWizardPresenceStore = create<SetupWizardPresenceState>((set) => ({
  mountedCount: 0,
  acquire: () => set((s) => ({ mountedCount: s.mountedCount + 1 })),
  release: () => set((s) => ({ mountedCount: Math.max(0, s.mountedCount - 1) })),
}));

/** True while at least one setup wizard is mounted. */
export function selectWizardOnScreen(s: SetupWizardPresenceState): boolean {
  return s.mountedCount > 0;
}
